// src/components/AccountMergeManager.jsx
import React, { useState, useEffect } from "react";
import {
  Users,
  AlertTriangle,
  Phone,
  Building2,
  X,
  ArrowRight,
  CheckCircle,
  XCircle,
  Loader,
  FileText,
  Briefcase,
  AlertCircle,
  Package,
} from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const AccountMergeManager = ({ onClose }) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [merging, setMerging] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(null);

  useEffect(() => {
    fetchDuplicates();
  }, []);

  const fetchDuplicates = async () => {
    try {
      setLoading(true);
      const response = await apiService.findDuplicates();
      setDuplicateGroups(response.data.duplicateGroups || []);
    } catch (error) {
      console.error("Error fetching duplicates:", error);
      alert("Failed to fetch duplicate accounts");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectGroup = async (group) => {
    setSelectedGroup(group);
    setSelectedSource(null);
    setSelectedTarget(null);
    setPreviewData(null);
  };

  const handleSelectAccounts = async () => {
    if (!selectedSource || !selectedTarget) {
      alert("Please select both source and target accounts");
      return;
    }
    if (selectedSource === selectedTarget) {
      alert("Cannot merge an account with itself");
      return;
    }
    try {
      setLoading(true);
      const response = await apiService.previewMerge(
        selectedSource,
        selectedTarget,
      );
      setPreviewData(response.data);
    } catch (error) {
      console.error("Error previewing merge:", error);
      alert("Failed to preview merge");
    } finally {
      setLoading(false);
    }
  };

  const handleMerge = async () => {
    if (
      !window.confirm(
        `Are you sure you want to merge "${previewData.source.name}" into "${previewData.target.name}"?\n\n` +
          `This will transfer ${previewData.totalRecords} related records and delete the source account. This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      setMerging(true);
      await apiService.mergeAccounts(selectedSource, selectedTarget, {
        mergeFields: true,
      });
      alert("Accounts merged successfully!");
      setSelectedGroup(null);
      setSelectedSource(null);
      setSelectedTarget(null);
      setPreviewData(null);
      await fetchDuplicates();
    } catch (error) {
      console.error("Error merging accounts:", error);
      alert(
        "Failed to merge accounts: " +
          (error.response?.data?.message || error.message),
      );
    } finally {
      setMerging(false);
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.9) return "text-green-400 bg-green-400/10";
    if (confidence >= 0.75) return "text-yellow-400 bg-yellow-400/10";
    return "text-red-400 bg-red-400/10";
  };

  const getReasonIcon = (reason) => {
    switch (reason) {
      case "phone":
        return <Phone size={16} className="text-blue-400" />;
      case "name":
        return <Building2 size={16} className="text-purple-400" />;
      default:
        return <Users size={16} className="text-gray-400" />;
    }
  };

  if (loading && duplicateGroups.length === 0) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div
          className={`rounded-lg border max-w-4xl w-full p-6 ${
            isDarkMode
              ? "bg-[#13161B] border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          <div className="flex items-center justify-center py-12">
            <Loader className="animate-spin text-blue-400" size={32} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div
        className={`rounded-lg border max-w-6xl w-full my-8 ${
          isDarkMode
            ? "bg-[#13161B] border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-6 border-b ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div>
            <h2
              className={`text-2xl font-bold flex items-center gap-2 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              <Users size={24} className="text-blue-400" />
              Account Merge Manager
            </h2>
            <p
              className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Found {duplicateGroups.length} potential duplicate groups
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
            }`}
          >
            <X
              size={20}
              className={isDarkMode ? "text-gray-400" : "text-gray-500"}
            />
          </button>
        </div>

        <div className="p-6">
          {duplicateGroups.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle
                size={64}
                className="mx-auto mb-4 text-green-400 opacity-50"
              />
              <h3
                className={`text-xl font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                No Duplicates Found
              </h3>
              <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                All accounts appear to be unique
              </p>
            </div>
          ) : !selectedGroup ? (
            /* Duplicate Groups List */
            <div className="space-y-3">
              {duplicateGroups.map((group, idx) => (
                <div
                  key={idx}
                  onClick={() => handleSelectGroup(group)}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    isDarkMode
                      ? "bg-gray-800/50 border-gray-700 hover:border-blue-500"
                      : "bg-gray-50 border-gray-200 hover:border-blue-400"
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {getReasonIcon(group.reason)}
                      <div>
                        <div
                          className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {group.reason === "phone"
                            ? "Phone Match"
                            : "Similar Names"}
                        </div>
                        <div
                          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          {group.accounts.length} accounts
                        </div>
                      </div>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${getConfidenceColor(group.confidence)}`}
                    >
                      {(group.confidence * 100).toFixed(0)}% match
                    </span>
                  </div>

                  <div className="space-y-1">
                    {group.accounts.map((account) => (
                      <div
                        key={account.id}
                        className={`text-sm flex items-center gap-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                      >
                        <div className="w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                          {account.name.charAt(0)}
                        </div>
                        {account.name}
                        {account.phone && (
                          <span
                            className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                          >
                            • {account.phone}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : !previewData ? (
            /* Account Selection */
            <div>
              <button
                onClick={() => setSelectedGroup(null)}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center gap-2"
              >
                ← Back to groups
              </button>

              <p
                className={`text-sm mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Select which account to delete (source) and which to keep
                (target):
              </p>

              <div className="space-y-3 mb-6">
                {selectedGroup.accounts.map((account) => (
                  <div
                    key={account.id}
                    className={`p-4 rounded-lg border transition-colors ${
                      isDarkMode
                        ? "bg-gray-800/50 border-gray-700"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-semibold">
                        {account.name.charAt(0)}
                      </div>
                      <div>
                        <div
                          className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {account.name}
                        </div>
                        {account.phone && (
                          <div
                            className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            {account.phone}
                          </div>
                        )}
                        {account.billingCity && (
                          <div
                            className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                          >
                            {account.billingCity}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSelectedSource(account.id);
                          if (selectedTarget === account.id)
                            setSelectedTarget(null);
                        }}
                        className={`flex-1 px-3 py-2 rounded text-sm font-medium transition-colors ${
                          selectedSource === account.id
                            ? "bg-red-600 text-white"
                            : isDarkMode
                              ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                        }`}
                      >
                        Source (Delete)
                      </button>
                      <button
                        onClick={() => {
                          setSelectedTarget(account.id);
                          if (selectedSource === account.id)
                            setSelectedSource(null);
                        }}
                        className={`flex-1 px-3 py-2 rounded text-sm font-medium transition-colors ${
                          selectedTarget === account.id
                            ? "bg-green-600 text-white"
                            : isDarkMode
                              ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                        }`}
                      >
                        Target (Keep)
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={handleSelectAccounts}
                disabled={!selectedSource || !selectedTarget || loading}
                className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-white"
              >
                {loading ? (
                  <Loader className="animate-spin" size={20} />
                ) : (
                  <>
                    Preview Merge
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </div>
          ) : (
            /* Merge Preview */
            <div>
              <button
                onClick={() => setPreviewData(null)}
                className="text-blue-400 hover:text-blue-300 mb-4 flex items-center gap-2"
              >
                ← Back to selection
              </button>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {/* Source Account */}
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <XCircle className="text-red-400" size={20} />
                    <span className="font-medium text-red-400">
                      Will Be Deleted
                    </span>
                  </div>
                  <div
                    className={`font-medium mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {previewData.source.name}
                  </div>
                  <div
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {previewData.source.id}
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex items-center justify-center">
                  <ArrowRight
                    className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                    size={32}
                  />
                </div>

                {/* Target Account */}
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="text-green-400" size={20} />
                    <span className="font-medium text-green-400">
                      Will Be Kept
                    </span>
                  </div>
                  <div
                    className={`font-medium mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {previewData.target.name}
                  </div>
                  <div
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {previewData.target.id}
                  </div>
                </div>
              </div>

              {/* Records to Move */}
              <div
                className={`rounded-lg p-4 mb-6 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-50"}`}
              >
                <h3
                  className={`font-medium mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Records to Transfer
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(previewData.recordsToMove).map(
                    ([key, count]) =>
                      count > 0 && (
                        <div
                          key={key}
                          className={`rounded p-3 ${isDarkMode ? "bg-gray-700/50" : "bg-gray-200/60"}`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            {key === "contacts" && (
                              <Users size={16} className="text-purple-400" />
                            )}
                            {key === "opportunities" && (
                              <Briefcase size={16} className="text-green-400" />
                            )}
                            {key === "cases" && (
                              <AlertCircle size={16} className="text-red-400" />
                            )}
                            {key === "projects" && (
                              <FileText size={16} className="text-blue-400" />
                            )}
                            {key === "assets" && (
                              <Package size={16} className="text-yellow-400" />
                            )}
                            <span
                              className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                            >
                              {count}
                            </span>
                          </div>
                          <div
                            className={`text-xs capitalize ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            {key.replace(/([A-Z])/g, " $1").trim()}
                          </div>
                        </div>
                      ),
                  )}
                </div>
                <div
                  className={`mt-4 pt-4 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                    >
                      Total Records
                    </span>
                    <span
                      className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {previewData.totalRecords}
                    </span>
                  </div>
                </div>
              </div>

              {/* Warnings */}
              {previewData.warnings.length > 0 && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="text-yellow-400" size={20} />
                    <span className="font-medium text-yellow-400">
                      Warnings
                    </span>
                  </div>
                  <div className="space-y-2">
                    {previewData.warnings.map((warning, idx) => (
                      <div
                        key={idx}
                        className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                      >
                        • {warning.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setPreviewData(null)}
                  className={`flex-1 px-6 py-3 rounded-lg font-medium transition-colors ${
                    isDarkMode
                      ? "bg-gray-700 hover:bg-gray-600 text-white"
                      : "bg-gray-200 hover:bg-gray-300 text-gray-900"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleMerge}
                  disabled={merging}
                  className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-white"
                >
                  {merging ? (
                    <Loader className="animate-spin" size={20} />
                  ) : (
                    <>
                      <AlertTriangle size={20} />
                      Confirm Merge
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountMergeManager;
